import { useMemo, useState } from 'react';
import { Eye, Heart, Users } from 'lucide-react';
import { formatNumber } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface StatsData {
  timestamp: string | number;
  watched_count: number | string;
  like_count: number | string;
  online_count: number | string;
}

interface StatsTrendChartProps {
  sessionId: string;
  statsData: StatsData[];
}

type Metric = 'watched' | 'like' | 'online';

function parseValue(value: number | string | undefined): number {
  if (value === undefined || value === null || value === '') return 0;
  const numeric = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (!Number.isFinite(numeric)) return 0;
  return typeof value === 'string' && value.includes('.') ? numeric * 10_000 : numeric;
}

function toTimestamp(value: string | number): number {
  if (typeof value === 'number') return value < 10_000_000_000 ? value * 1000 : value;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric < 10_000_000_000 ? numeric * 1000 : numeric;
  return new Date(value).getTime();
}

export default function StatsTrendChart({ statsData }: StatsTrendChartProps) {
  const [activeMetric, setActiveMetric] = useState<Metric>('watched');

  const chartData = useMemo(() => {
    const sorted = [...(statsData || [])].sort(
      (left, right) => toTimestamp(left.timestamp) - toTimestamp(right.timestamp)
    );
    return {
      watched: sorted.map((point) => parseValue(point.watched_count)),
      like: sorted.map((point) => parseValue(point.like_count)),
      online: sorted.map((point) => parseValue(point.online_count)),
      timestamps: sorted.map((point) => toTimestamp(point.timestamp)),
      timeLabels: sorted.map((point) => {
        const date = new Date(toTimestamp(point.timestamp));
        return Number.isNaN(date.getTime())
          ? '--:--'
          : date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
      }),
    };
  }, [statsData]);

  const metricConfig: Record<Metric, {
    label: string;
    icon: typeof Eye;
    color: string;
    data: number[];
  }> = {
    watched: { label: '累计观看', icon: Eye, color: 'var(--chart-watched)', data: chartData.watched },
    like: { label: '点赞数', icon: Heart, color: 'var(--chart-like)', data: chartData.like },
    online: { label: '在线人数', icon: Users, color: 'var(--chart-online)', data: chartData.online },
  };

  const active = metricConfig[activeMetric];
  const nonZeroValues = active.data.filter((value) => value > 0);
  const peak = Math.max(...active.data, 0);
  const average = active.data.length
    ? Math.round(active.data.reduce((sum, value) => sum + value, 0) / active.data.length)
    : 0;
  const minimum = nonZeroValues.length ? Math.min(...nonZeroValues) : 0;
  const intervalSeconds = chartData.timestamps.length > 1
    ? Math.max(1, Math.floor(
        (chartData.timestamps[chartData.timestamps.length - 1] - chartData.timestamps[0]) /
        (chartData.timestamps.length - 1) /
        1000
      ))
    : 0;

  const renderChart = () => {
    if (!active.data.length) {
      return <div className="flex h-64 items-center justify-center text-sm text-[var(--text-muted)]">暂无统计数据</div>;
    }

    const width = 900;
    const height = 300;
    const paddingX = 54;
    const paddingY = 32;
    const max = Math.max(...active.data, 1);
    const min = Math.min(...nonZeroValues, 0);
    const range = max - min || 1;
    const points = active.data.map((value, index) => ({
      x: paddingX + (index / Math.max(active.data.length - 1, 1)) * (width - paddingX * 2),
      y: height - paddingY - ((value - min) / range) * (height - paddingY * 2),
    }));
    const pathData = 'M ' + points.map((point) => point.x + ',' + point.y).join(' L ');
    const labelStep = Math.max(1, Math.ceil(chartData.timeLabels.length / 7));

    return (
      <svg viewBox={'0 0 ' + width + ' ' + height} className="h-auto min-w-[620px] w-full" role="img" aria-label={active.label + '趋势图'}>
        {[0, 1, 2, 3, 4].map((index) => {
          const y = paddingY + (index / 4) * (height - paddingY * 2);
          return (
            <g key={index}>
              <line x1={paddingX} y1={y} x2={width - paddingX} y2={y} stroke="var(--structural-border)" strokeWidth="1" />
              <text x={paddingX - 10} y={y + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)" fontFamily="IBM Plex Mono, monospace">
                {formatNumber(Math.floor(max - (index / 4) * range))}
              </text>
            </g>
          );
        })}
        <path d={pathData} fill="none" stroke={active.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((point, index) => active.data[index] > 0 && (
          <circle key={index} cx={point.x} cy={point.y} r="3.5" fill={active.color}>
            <title>{chartData.timeLabels[index] + ' · ' + formatNumber(active.data[index])}</title>
          </circle>
        ))}
        {chartData.timeLabels.map((label, index) => {
          if (index % labelStep !== 0 && index !== chartData.timeLabels.length - 1) return null;
          const point = points[index];
          return (
            <text key={index} x={point.x} y={height - 8} textAnchor="middle" fontSize="10" fill="var(--text-muted)" fontFamily="IBM Plex Mono, monospace">
              {label}
            </text>
          );
        })}
      </svg>
    );
  };

  return (
    <section className="console-panel" aria-labelledby="stats-trend-title">
      <div className="console-panel-header">
        <div>
          <p className="console-kicker">STATISTICS · 统计趋势</p>
          <h2 id="stats-trend-title" className="mt-1 text-sm font-bold text-[var(--text-ink)]">直播过程指标</h2>
        </div>
        <p className="font-mono text-[11px] text-[var(--text-muted)]">
          {statsData.length} 个数据点{intervalSeconds ? ' · 约 ' + intervalSeconds + ' 秒/次' : ''}
        </p>
      </div>

      <div className="border-b border-[var(--border-subtle)] p-3">
        <div className="grid grid-cols-3 gap-2" role="tablist" aria-label="统计指标">
          {(Object.keys(metricConfig) as Metric[]).map((metric) => {
            const config = metricConfig[metric];
            const Icon = config.icon;
            return (
              <button
                key={metric}
                type="button"
                role="tab"
                aria-selected={activeMetric === metric}
                onClick={() => setActiveMetric(metric)}
                className={cn(
                  'flex min-h-11 items-center justify-center gap-2 rounded-md border px-3 text-xs transition-colors',
                  activeMetric === metric
                    ? 'border-[var(--border-strong)] bg-[var(--surface-selected)] text-[var(--text-ink)]'
                    : 'border-transparent text-[var(--text-muted)] hover:bg-[var(--panel-raised)] hover:text-[var(--text-soft)]'
                )}
              >
                <Icon className="h-4 w-4" style={{ color: activeMetric === metric ? config.color : undefined }} />
                {config.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 md:p-[18px]">
        <dl className="grid grid-cols-3 gap-2.5">
          <div className="console-metric"><dt className="console-metric-label">峰值</dt><dd className="console-metric-value mt-1">{formatNumber(peak)}</dd></div>
          <div className="console-metric"><dt className="console-metric-label">平均</dt><dd className="console-metric-value mt-1">{formatNumber(average)}</dd></div>
          <div className="console-metric"><dt className="console-metric-label">最低非零值</dt><dd className="console-metric-value mt-1">{formatNumber(minimum)}</dd></div>
        </dl>
        <div className="mt-4 overflow-x-auto rounded-md border border-[var(--border-subtle)] bg-[var(--panel-deep)] p-2 md:p-4">
          {renderChart()}
        </div>
      </div>
    </section>
  );
}
